import { Icon } from "@web-app/components";
import type { FilterOperator, GUIBuilderFilter } from "@web-app/types";

import { RegionFilterValue } from "./RegionFilterValue";
import { GUIDED_FILTER_FIELDS } from "./guidedBuilderConfig";

interface FilterSectionProps {
  filters: GUIBuilderFilter[];
  onFiltersChange: (filters: GUIBuilderFilter[]) => void;
}

export function FilterSection({
  filters,
  onFiltersChange,
}: FilterSectionProps): JSX.Element {
  const addFilter = (): void => {
    const newFilter: GUIBuilderFilter = {
      field: GUIDED_FILTER_FIELDS[0].id,
      operator: "equals",
      values: [],
    };
    onFiltersChange([...filters, newFilter]);
  };

  const removeFilter = (index: number): void => {
    const newFilters = filters.filter((_, i) => i !== index);
    onFiltersChange(newFilters);
  };

  const updateFilter = (
    index: number,
    updates: Partial<GUIBuilderFilter>,
  ): void => {
    const newFilters = filters.map((filter, i) => {
      if (i === index) {
        return { ...filter, ...updates };
      }
      return filter;
    });
    onFiltersChange(newFilters);
  };

  const handleFieldChange = (index: number, fieldId: string): void => {
    // Reset operator and values when field changes
    const fieldConfig = GUIDED_FILTER_FIELDS.find((f) => f.id === fieldId);
    if (fieldConfig) {
      updateFilter(index, {
        field: fieldId,
        operator: fieldConfig.operators[0].value as FilterOperator,
        values: [],
      });
    }
  };

  const handleOperatorChange = (index: number, operator: string): void => {
    updateFilter(index, { operator: operator as FilterOperator });
  };

  const handleValueChange = (index: number, value: string): void => {
    const filter = filters[index];
    const fieldConfig = GUIDED_FILTER_FIELDS.find((f) => f.id === filter.field);

    if (!fieldConfig) return;

    updateFilter(index, { values: value ? [value] : [] });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Filter By (Optional)
        </label>
        <button
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
          onClick={addFilter}
          type="button"
        >
          <Icon className="w-4 h-4 mr-1" name="fasPlus" />
          Add Filter
        </button>
      </div>

      {filters.length === 0 && (
        <p className="text-sm text-gray-500 italic">
          No filters applied. Click "Add Filter" to filter your data.
        </p>
      )}

      {filters.map((filter, index) => {
        const fieldConfig = GUIDED_FILTER_FIELDS.find(
          (f) => f.id === filter.field,
        );
        if (!fieldConfig) return null;

        const isRegionFilter = filter.field === "state";

        return (
          <div
            className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200"
            key={index}
          >
            {/* Field Selector */}
            <div className="flex-1">
              <select
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={(e) => handleFieldChange(index, e.target.value)}
                value={filter.field}
              >
                {GUIDED_FILTER_FIELDS.map((field) => (
                  <option key={field.id} value={field.id}>
                    {field.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Operator Selector */}
            <div className="flex-1">
              <select
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={(e) => handleOperatorChange(index, e.target.value)}
                value={filter.operator}
              >
                {fieldConfig.operators.map((op) => (
                  <option key={op.value} value={op.value}>
                    {op.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Value Selector */}
            <div className="flex-1">
              {isRegionFilter ? (
                <RegionFilterValue
                  values={filter.values}
                  onChange={(values) => updateFilter(index, { values })}
                />
              ) : (
                <select
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onChange={(e) => handleValueChange(index, e.target.value)}
                  value={filter.values[0] || ""}
                >
                  <option value="">Select value...</option>
                  {fieldConfig.values.map((val) => (
                    <option key={val.value} value={val.value}>
                      {val.label}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Remove Button */}
            <button
              className="flex-shrink-0 p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
              onClick={() => removeFilter(index)}
              type="button"
            >
              <Icon className="w-4 h-4" name="fasTrash" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
